import { useCallback, useEffect, useRef, useState } from 'react'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { DocumentDraft, ReviewDocument } from './result-model'

export function useDraftAutosave(reviewId: string, documents: ReviewDocument[], onSaved: (document: ReviewDocument) => void) {
  const [drafts, setDrafts] = useState<Record<string, DocumentDraft>>({})
  const [savingId, setSavingId] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [revision, setRevision] = useState(0)
  const draftsRef = useRef(drafts)
  const versions = useRef<Record<string, number>>({})
  const documentsRef = useRef(documents)
  const callbackRef = useRef(onSaved)
  const blocked = useRef(new Set<string>())
  const running = useRef<Promise<boolean> | null>(null)
  const mounted = useRef(true)
  documentsRef.current = documents
  callbackRef.current = onSaved
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const change = useCallback((id: string, update: (draft: DocumentDraft) => DocumentDraft) => {
    const document = documentsRef.current.find(item => item.id === id)
    if (!document) return
    if (!draftsRef.current[id]) versions.current[id] = document.version
    const next = { ...draftsRef.current, [id]: update(draftsRef.current[id] || document.draft) }
    draftsRef.current = next
    setDrafts(next)
  }, [])

  const persist = useCallback(async (id: string, status: 'review' | 'confirmed' = 'review'): Promise<boolean> => {
    if (running.current) {
      await running.current
      if (blocked.current.has(id)) return false
      return persist(id, status)
    }
    const document = documentsRef.current.find(item => item.id === id)
    const snapshot = draftsRef.current[id] || document?.draft
    if (!document || !snapshot || (status === 'review' && !draftsRef.current[id])) return true
    setSavingId(id)
    const operation = (async () => {
      try {
        const response = await fetchAPI(`/api/supply/reviews/${reviewId}/documents/${id}`, { method: 'PUT', body: JSON.stringify({ version: draftsRef.current[id] ? versions.current[id] ?? document.version : document.version, draft: snapshot, status }) })
        if (response.error || response.errors || !response.data?.id) throw new Error(response.error || response.errors?.join(', ') || 'Не удалось сохранить проверку')
        if (!mounted.current) return true
        versions.current[id] = response.data.version
        documentsRef.current = documentsRef.current.map(item => item.id === id ? response.data : item)
        callbackRef.current(response.data)
        if (draftsRef.current[id] === snapshot) {
          const next = { ...draftsRef.current }; delete next[id]
          draftsRef.current = next; setDrafts(next)
        }
        blocked.current.delete(id)
        setErrors(value => { const next = { ...value }; delete next[id]; return next })
        return true
      } catch (cause) {
        if (mounted.current) {
          blocked.current.add(id)
          setErrors(value => ({ ...value, [id]: cause instanceof Error ? cause.message : 'Не удалось сохранить проверку' }))
        }
        return false
      }
    })()
    running.current = operation
    try { return await operation }
    finally {
      running.current = null
      if (mounted.current) { setSavingId(''); setRevision(value => value + 1) }
    }
  }, [reviewId])

  const flush = useCallback(async () => {
    if (running.current && !await running.current) return false
    for (const id of Object.keys(draftsRef.current)) if (!await persist(id)) return false
    return true
  }, [persist])
  const discard = useCallback(() => {
    draftsRef.current = {}; versions.current = {}; blocked.current.clear()
    setDrafts({}); setErrors({})
  }, [])

  useEffect(() => {
    const id = Object.keys(drafts).find(key => !blocked.current.has(key))
    if (!id) return
    const timer = setTimeout(() => { void persist(id) }, 800)
    return () => clearTimeout(timer)
  }, [drafts, revision, persist])
  return { drafts, change, persist, flush, discard, savingId, errors }
}
